// src/components/doctorsJournal/JournalEntryCard.js
import React from 'react';
import { Calendar, User, Edit, Trash2, Lock } from 'lucide-react';

export const JournalEntryCard = ({ entry, isLocked, onEdit, onDelete, onClick, onCustomerClick }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  };

  const getMoodLabel = (mood) => {
    const labels = {
      very_distressed: 'במצוקה רבה',
      distressed: 'במצוקה',
      neutral: 'ניטרלי',
      good: 'טוב',
      excellent: 'מצוין'
    };
    return labels[mood] || mood;
  };

  return (
    <div
      className="group relative bg-white rounded-2xl border border-gray-200 hover:border-pink-300 transition-all hover:shadow-lg overflow-hidden cursor-pointer"
      onClick={onClick}>
      {/* Locked Overlay */}
      {isLocked && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/95 to-gray-800/95 backdrop-blur-sm z-10 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-3 bg-gray-700 rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-gray-300" />
            </div>
            <p className="text-white font-semibold text-lg mb-1">רשומה מוצפנת</p>
            <p className="text-gray-300 text-sm">נדרשת סיסמה לצפייה</p>
          </div>
        </div>
      )}

      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-3">
            <div>
              <button
                onClick={() => onCustomerClick?.(entry.customer)}
                className="text-base font-semibold text-gray-900 hover:text-pink-600 transition-colors"
              >
                {entry.customer?.full_name || 'לקוח לא ידוע'}
              </button>
              <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                <Calendar className="w-3.5 h-3.5" />
                <span>{formatDate(entry.entry_date || entry.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          {!isLocked && (
            <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(entry)
                }}
                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(entry)
                }}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        {!isLocked && entry.sessionData && (
          <div className="space-y-2 text-xs">
            {/* Session Info */}
            <div className="flex gap-4 text-gray-600">
              <span>מפגש #{entry.session_number}</span>
              <span>•</span>
              <span>{entry.sessionData.duration_minutes} דקות</span>
              <span>•</span>
              <span>מצב רוח {getMoodLabel(entry.sessionData.mood_assessment)}</span>
            </div>

            {/* Session Notes Preview */}
            {entry.sessionData.session_notes && (
              <p className="text-gray-700 leading-relaxed">
                {entry.sessionData.session_notes.split(' ').slice(0, 50).join(' ')}
                {entry.sessionData.session_notes.split(' ').length > 50 && '...'}
              </p>
            )}

            {/* Interventions */}
            {entry.sessionData.interventions_used?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {entry.sessionData.interventions_used.slice(0, 3).map((int, i) => (
                  <span key={i} className="px-2 py-1 bg-pink-50 text-pink-700 rounded text-xs">
                    {int}
                  </span>
                ))}
                {entry.sessionData.interventions_used.length > 3 && (
                  <span className="px-2 py-1 bg-gray-50 text-gray-600 rounded text-xs">
                    +{entry.sessionData.interventions_used.length - 3}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom gradient accent */}
      <div className="h-1 bg-gradient-to-l from-pink-400 to-purple-400" />
    </div>
  );
};